// Settings -> Worst offenders: the weights behind the "Worst offenders" order of the
// Cluster insights and Insights navigators (Safety/offenders.js). Applies at once. The
// clusters' deficiency weights come first — they rank the clusters with or without crash
// data; the crash weights follow.
import React from 'react';
import { Modal } from '../Overview/OverviewDialogs';
import { ANALYSIS_NAMES } from '../Overview/overviewModel';
import { DEFAULT_OFFENDER_WEIGHTS, OFFENDER_ANALYSES, OFFENDER_SEVERITIES, clusterLogic, crashLogic } from '../Safety/offenders';

const Row = ({ id, label, hint, value, onChange, step = 1 }) => (
  <div className="so-rule-row">
    <label htmlFor={id} style={{ minWidth: '18rem' }}>{label}{hint && <span className="safety-muted"> · {hint}</span>}</label>
    <span className="so-rule-num">
      <input id={id} type="number" min={0} step={step} value={value} style={{ width: 110 }}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))} />
    </span>
  </div>
);

export default function OffendersDialog({ weights, onChange, onClose }) {
  const set = (group, key, v) => onChange({ ...weights, [group]: { ...weights[group], [key]: v } });
  const setIn = (key, sub, v) => onChange({ ...weights, cluster: { ...weights.cluster, [key]: { ...weights.cluster[key], [sub]: v } } });
  const isDefault = JSON.stringify(weights) === JSON.stringify(DEFAULT_OFFENDER_WEIGHTS);
  return (
    <Modal title="Worst offenders" onClose={onClose} tall>
      <p className="safety-muted safety-modal-lede ov-lede-compact">
        How the Cluster insights and the Insights rank what they step through when sorted by
        &quot;Worst offenders&quot;: a weighted score, highest first. Kept in this browser.
      </p>
      <section className="so-rule-block">
        <h3>Multi-deficiency clusters (Cluster insights)</h3>
        <p className="safety-muted">From the deficiencies — these rank the clusters with or without crash data.</p>
        {OFFENDER_SEVERITIES.map((s) => (
          <Row key={s} id={`so-w-sev-${s}`} label={`per ${s} deficiency run`} value={weights.cluster.severity[s]} step={1}
            onChange={(v) => setIn('severity', s, v)} />
        ))}
        {OFFENDER_ANALYSES.map((a) => (
          <Row key={a} id={`so-w-an-${a}`} label={`${a} in the cluster`} hint={ANALYSIS_NAMES[a]} value={weights.cluster.analysis[a]}
            step={5} onChange={(v) => setIn('analysis', a, v)} />
        ))}
        <p className="safety-muted">With crash data loaded, also:</p>
        <Row id="so-w-cl-cost" label="per $1,000 of crash cost on the cluster" hint="KABCO comprehensive cost" value={weights.cluster.cost} step={0.1}
          onChange={(v) => set('cluster', 'cost', v)} />
        <Row id="so-w-cl-corr" label="per correlated crash" value={weights.cluster.correlatedCrash} step={1}
          onChange={(v) => set('cluster', 'correlatedCrash', v)} />
        <p className="safety-muted">{clusterLogic(weights)}</p>
      </section>
      <section className="so-rule-block">
        <h3>Crashes (Insights)</h3>
        <Row id="so-w-crash-cost" label="per $1,000 of crash cost" hint="KABCO comprehensive cost of its injury severity"
          value={weights.crash.cost} step={0.1} onChange={(v) => set('crash', 'cost', v)} />
        <Row id="so-w-crash-finding" label="per deficiency at its location" value={weights.crash.perFinding}
          step={1} onChange={(v) => set('crash', 'perFinding', v)} />
        <p className="safety-muted">{crashLogic(weights)}</p>
      </section>
      <div className="safety-modal-foot">
        <span className="safety-muted">Defaults: severe 25 · moderate 10 · minor 5 per run; 50 per analysis; 1 per $1,000 of crash cost; 25 per correlated crash.</span>
        <div className="row-actions">
          <button type="button" className="safety-btn safety-btn-ghost" disabled={isDefault}
            onClick={() => onChange(JSON.parse(JSON.stringify(DEFAULT_OFFENDER_WEIGHTS)))}>Reset to defaults</button>
          <button type="button" className="safety-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </Modal>
  );
}
